<script setup>
/**
 * Admin Promotions View
 * Two-pane layout for managing sales promotions
 * Left: Merakez (Assortments) selector
 * Right: Sales list for selected merakez
 */
import { ref, computed, watch, onMounted } from 'vue'
import MerakezSelector from '../../components/admin/promotions/MerakezSelector.vue'
import SalesList from '../../components/admin/promotions/SalesList.vue'
import PromotionEditor from '../../components/admin/promotions/PromotionEditor.vue'
import {
  getAssortmentsIndex,
  getSalesByMerakez,
  duplicateSale,
} from '../../../core/services/sales.js'
import { exportPromotionsToExcel } from '../../../core/services/promotionExport.js'
import { useToast } from '../../../core/composables/useToast.js'

const { addToast } = useToast()

// ============================================
// Assortments State
// ============================================
const assortments = ref([])
const assortmentsLoading = ref(false)
const assortmentsError = ref(null)

// ============================================
// Selection State
// ============================================
const selectedMerakez = ref(null)
const selectorExpanded = ref(true) // For mobile collapsible

// ============================================
// Sales State
// ============================================
const sales = ref([])
const salesLoading = ref(false)
const salesError = ref(null)

// ============================================
// Editor State
// ============================================
const editorOpen = ref(false)
const editingSaleId = ref(null)

// ============================================
// Load Assortments
// ============================================
async function loadAssortments() {
  assortmentsLoading.value = true
  assortmentsError.value = null

  try {
    assortments.value = await getAssortmentsIndex()
    // Auto-select the first merakez if none is selected
    if (!selectedMerakez.value && assortments.value.length > 0) {
      selectedMerakez.value = assortments.value[0].id
    }
  } catch (err) {
    console.error('[AdminPromotions] Failed to load assortments:', err)
    assortmentsError.value = 'שגיאה בטעינת המגוונים'
  } finally {
    assortmentsLoading.value = false
  }
}

// ============================================
// Load Sales for Selected Merakez
// ============================================
async function loadSales(merakez) {
  if (!merakez) {
    sales.value = []
    return
  }

  salesLoading.value = true
  salesError.value = null

  try {
    sales.value = await getSalesByMerakez(merakez)
  } catch (err) {
    console.error('[AdminPromotions] Failed to load sales:', err)
    salesError.value = 'שגיאה בטעינת המבצעים'
  } finally {
    salesLoading.value = false
  }
}

// ============================================
// Handle Merakez Selection
// ============================================
function handleMerakezSelect(merakez) {
  selectedMerakez.value = merakez
  // Collapse selector on mobile after selection
  if (window.innerWidth <= 900) {
    selectorExpanded.value = false
  }
}

// Toggle selector on mobile
function toggleSelector() {
  selectorExpanded.value = !selectorExpanded.value
}

// Get display name for selected merakez
function getSelectedMerakezName() {
  if (!selectedMerakez.value) return null
  const found = assortments.value.find((a) => a.id === selectedMerakez.value)
  return found?.name || `מרכז ${selectedMerakez.value}`
}

// Watch for merakez changes and load sales
watch(selectedMerakez, (newMerakez) => {
  loadSales(newMerakez)
})

// ============================================
// Editor Handlers
// ============================================
function handleCreate() {
  editingSaleId.value = null
  editorOpen.value = true
}

function handleEdit(saleId) {
  editingSaleId.value = saleId
  editorOpen.value = true
}

function handleEditorClose() {
  editorOpen.value = false
  editingSaleId.value = null
}

function handleSaved() {
  // Refresh the sales list
  loadSales(selectedMerakez.value)
}

function handleDeleted() {
  // Refresh the sales list
  loadSales(selectedMerakez.value)
}

// ============================================
// Export State & Handler
// ============================================
const exporting = ref(false)

async function handleExport(saleIds) {
  if (!saleIds?.length) return

  exporting.value = true
  try {
    const count = await exportPromotionsToExcel(saleIds, {
      merakez: selectedMerakez.value,
      merakezName: getSelectedMerakezName(),
    })
    addToast({ message: `יוצאו ${count} מבצעים לאקסל`, type: 'success' })
  } catch (err) {
    console.error('[AdminPromotions] Export failed:', err)
    addToast({ message: 'שגיאה בייצוא לאקסל', type: 'error' })
  } finally {
    exporting.value = false
  }
}

// ============================================
// Duplicate State & Handlers
// ============================================
const duplicateDialogOpen = ref(false)
const duplicatingSaleId = ref(null)
const duplicateSearch = ref('')
const duplicateLoading = ref(false)

const filteredDuplicateAssortments = computed(() => {
  const q = duplicateSearch.value.trim().toLowerCase()
  return assortments.value.filter((a) => {
    // Exclude the current merakez
    if (a.id === selectedMerakez.value) return false
    if (!q) return true
    return a.id.toLowerCase().includes(q) || (a.name && a.name.toLowerCase().includes(q))
  })
})

function handleDuplicate(saleId) {
  duplicatingSaleId.value = saleId
  duplicateSearch.value = ''
  duplicateDialogOpen.value = true
}

function closeDuplicateDialog() {
  duplicateDialogOpen.value = false
  duplicatingSaleId.value = null
  duplicateSearch.value = ''
}

async function confirmDuplicate(targetMerakez) {
  if (!duplicatingSaleId.value || !targetMerakez) return

  duplicateLoading.value = true
  try {
    await duplicateSale(duplicatingSaleId.value, targetMerakez)
    addToast({ message: `המבצע שוכפל בהצלחה למרכז ${targetMerakez}`, type: 'success' })
    closeDuplicateDialog()
    // Refresh current list in case user switches to see the duplicate
    loadSales(selectedMerakez.value)
  } catch (err) {
    console.error('[AdminPromotions] Failed to duplicate sale:', err)
    addToast({ message: 'שגיאה בשכפול המבצע', type: 'error' })
  } finally {
    duplicateLoading.value = false
  }
}

// ============================================
// Lifecycle
// ============================================
onMounted(() => {
  loadAssortments()
})
</script>

<template>
  <div class="admin-promotions">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">ניהול מבצעים</h1>
      <p class="page-subtitle">בחירת לקוח מרכז וצפייה במבצעים המשויכים אליו</p>
    </div>

    <!-- Two-Pane Layout -->
    <div class="promotions-layout">
      <!-- Mobile Selector Toggle -->
      <button class="mobile-selector-toggle" @click="toggleSelector">
        <span class="toggle-label">
          <template v-if="selectedMerakez">
            מרכז: <strong>{{ selectedMerakez }}</strong>
            <span v-if="getSelectedMerakezName()" class="toggle-name">{{
              getSelectedMerakezName()
            }}</span>
          </template>
          <template v-else> בחר לקוח מרכז </template>
        </span>
        <svg
          class="toggle-icon"
          :class="{ expanded: selectorExpanded }"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <!-- Left Pane: Merakez Selector -->
      <div class="pane pane-left" :class="{ collapsed: !selectorExpanded }">
        <MerakezSelector
          :assortments="assortments"
          :selected-merakez="selectedMerakez"
          :loading="assortmentsLoading"
          :error="assortmentsError"
          @select="handleMerakezSelect"
        />
      </div>

      <!-- Right Pane: Sales List -->
      <div class="pane pane-right">
        <SalesList
          :sales="sales"
          :selected-merakez="selectedMerakez"
          :loading="salesLoading"
          :error="salesError"
          :exporting="exporting"
          @create="handleCreate"
          @edit="handleEdit"
          @delete="handleEdit"
          @duplicate="handleDuplicate"
          @export="handleExport"
        />
      </div>
    </div>

    <!-- Promotion Editor Drawer -->
    <PromotionEditor
      :is-open="editorOpen"
      :sale-id="editingSaleId"
      :merakez="selectedMerakez"
      @close="handleEditorClose"
      @saved="handleSaved"
      @deleted="handleDeleted"
    />

    <!-- Duplicate Merakez Picker Dialog -->
    <Teleport to="body">
      <Transition name="dialog-fade">
        <div v-if="duplicateDialogOpen" class="dialog-overlay" @click.self="closeDuplicateDialog">
          <div class="dialog-panel">
            <div class="dialog-header">
              <h3 class="dialog-title">שכפול מבצע למרכז אחר</h3>
              <button class="dialog-close" @click="closeDuplicateDialog">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="dialog-search">
              <input
                v-model="duplicateSearch"
                type="text"
                class="dialog-search-input"
                placeholder="חיפוש לפי מספר או שם מרכז..."
                autofocus
              />
            </div>

            <div class="dialog-list">
              <div
                v-for="item in filteredDuplicateAssortments"
                :key="item.id"
                class="dialog-list-item"
                :class="{ disabled: duplicateLoading }"
                @click="!duplicateLoading && confirmDuplicate(item.id)"
              >
                <span class="dialog-item-id">{{ item.id }}</span>
                <span v-if="item.name" class="dialog-item-name">{{ item.name }}</span>
              </div>
              <div v-if="filteredDuplicateAssortments.length === 0" class="dialog-empty">
                אין תוצאות
              </div>
            </div>

            <div v-if="duplicateLoading" class="dialog-loading">
              <div class="loading-spinner"></div>
              <span>משכפל מבצע...</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.admin-promotions {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-lg);
  overflow: hidden;
}

/* Page Header */
.page-header {
  flex-shrink: 0;
  margin-bottom: var(--spacing-lg);
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.page-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0;
}

/* Two-Pane Layout */
.promotions-layout {
  display: flex;
  align-items: stretch;
  gap: var(--spacing-lg);
  flex: 1 1 0;
  height: 0;
  overflow: hidden;
}

.pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.pane-left {
  width: 320px;
  flex-shrink: 0;
  height: 100%;
}

.pane-right {
  flex: 1;
  min-width: 0;
  height: 100%;
}

/* Mobile Selector Toggle - Hidden on desktop */
.mobile-selector-toggle {
  display: none;
}

/* Responsive: Stack on mobile */
@media (max-width: 900px) {
  .admin-promotions {
    padding: var(--spacing-md);
  }

  .promotions-layout {
    flex-direction: column;
    gap: 0;
  }

  /* Mobile Toggle Button */
  .mobile-selector-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--spacing-md);
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    font-family: var(--font-family);
    font-size: var(--font-size-sm);
    color: var(--text-primary);
    cursor: pointer;
    flex-shrink: 0;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .toggle-name {
    color: var(--text-muted);
    font-size: 12px;
  }

  .toggle-icon {
    color: var(--text-muted);
    transition: transform var(--transition-fast);
  }

  .toggle-icon.expanded {
    transform: rotate(180deg);
  }

  .pane-left {
    width: 100%;
    max-height: 300px;
    flex-shrink: 0;
    overflow: hidden;
    margin-top: var(--spacing-sm);
    transition:
      max-height 0.3s ease,
      opacity 0.3s ease,
      margin 0.3s ease;
  }

  .pane-left.collapsed {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    pointer-events: none;
  }

  .pane-right {
    flex: 1;
    min-height: 0;
    margin-top: var(--spacing-sm);
  }
}

/* Duplicate Dialog */
.dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  padding: var(--spacing-lg);
}

.dialog-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  max-height: 70vh;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.dialog-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
}

.dialog-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.dialog-search {
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.dialog-search-input {
  width: 100%;
  padding: 8px 12px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  outline: none;
}

.dialog-search-input:focus {
  border-color: var(--color-primary);
}

.dialog-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.dialog-list-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.dialog-list-item:hover {
  background: var(--bg-secondary);
}

.dialog-list-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.dialog-item-id {
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  flex-shrink: 0;
}

.dialog-item-name {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-empty {
  padding: var(--spacing-xl);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.dialog-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  flex-shrink: 0;
}

.dialog-loading .loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dialog Transition */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-active .dialog-panel,
.dialog-fade-leave-active .dialog-panel {
  transition: transform 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-fade-enter-from .dialog-panel,
.dialog-fade-leave-to .dialog-panel {
  transform: scale(0.95);
}
</style>
